import { useMemo } from "react";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { DateTimeFormatStyle } from "@hopper-i18n/formatter";
import { TripsCard } from "@tenant/trips";
import { Icon, Pill, PillTone } from "@tenant/ui";
import clsx from "clsx";
import dayjs from "dayjs";
import styles from "./CarReservationCard.module.scss";
import type {
  GroundBookingContext,
  CarsProductState,
} from "@/utils/commerce/types";
import { getVehicleTitle } from "@/utils/commerce/vehicleLabels";

export interface CarReservationCardProps extends PlatformComponentProps {
  reservation: GroundBookingContext;
  onClick: () => void;
}

export const CarReservationCard = (props: CarReservationCardProps) => {
  const [block, cn] = useModuleBEM(styles, "CarReservationCard");
  const { t, formatDateTime } = useI18nContext();

  const { className, reservation, onClick } = props;

  const { vehicle, pickUp, dropOff } = reservation.productDetails;

  const pickupDate = formatDateTime(
    dayjs(pickUp.dateTime).toDate(),
    DateTimeFormatStyle.ShortMonthDayShortWeekdayTime
  );
  const dropoffDate = formatDateTime(
    dayjs(dropOff.dateTime).toDate(),
    DateTimeFormatStyle.ShortMonthDayShortWeekdayTime
  );

  const pill = useMemo(() => {
    const state: CarsProductState = reservation.productState;

    switch (state) {
      case "cancelled":
        return (
          <Pill tone={PillTone.error}>
            {t("trips.tripCard.status.canceled")}
          </Pill>
        );
      case "pending":
        return (
          <Pill tone={PillTone.caution}>
            {t("trips.tripCard.status.pending")}
          </Pill>
        );
      case "failure":
        return (
          <Pill tone={PillTone.error}>
            {t("trips.tripCard.status.failure")}
          </Pill>
        );
      case "confirmed":
      case "unknown":
      default:
        return null;
    }
  }, [reservation.productState, t]);

  return (
    <TripsCard
      showViewDetails={true}
      showBookingId={true}
      bookingId={
        reservation.customerConfirmationId || reservation.groundBookingId
      }
      className={clsx(block, className)}
      product={
        <>
          <Icon
            iconName={IconNameEnum.carReservation}
            className={cn("product-icon")}
          />{" "}
          {t("trips.tripCard.carRental")}
        </>
      }
      title={pickUp.location.name}
      status={pill}
      onClick={onClick}
    >
      <div className={cn("product-info")}>
        <div className={cn("rental-data")}>
          <div className={cn("data")}>
            <span className={cn("data-label")}>
              {t("trips.tripCard.pickupDate")}:
            </span>{" "}
            {pickupDate}
          </div>
          <div className={cn("data")}>
            <span className={cn("data-label")}>
              {t("trips.tripCard.dropoffDate")}:
            </span>{" "}
            {dropoffDate}
          </div>
          <div className={cn("data")}>
            <span className={cn("data-label")}>
              {t("trips.tripCard.vehicleType")}:
            </span>{" "}
            {getVehicleTitle(
              t,
              vehicle.category,
              vehicle.vehicleType,
              vehicle.name
            )}
          </div>
        </div>
      </div>
    </TripsCard>
  );
};
